import { cva } from 'class-variance-authority';

export const chipVariants = cva(
  'text-color-text-dark border-color-grey-dark hover:bg-color-grey-light bg-color-background-base focus:border-color-text-dark inline-flex cursor-pointer flex-row justify-center rounded-full border transition-colors duration-300 focus:border-2 active:shadow-xs',
  {
    variants: {
      /**
       * for all colors, the following classes are applied:
       * - text-color-<color>-dark
       * - border-color-<color>-dark
       * - bg-color-background-base
       * - hover:bg-color-<color>-light
       * - hover:text-color-<color>-dark
       */
      color: {
        error:
          'text-color-error-dark border-color-error-dark bg-color-background-base hover:bg-color-error-light hover:text-color-error-dark',
        info: 'text-color-info-dark border-color-info-dark bg-color-background-base hover:bg-color-info-light hover:text-color-info-dark',
        primary:
          'text-color-primary-dark border-color-primary-dark bg-color-background-base hover:bg-color-primary-light hover:text-color-primary-dark',
        secondary:
          'text-color-secondary-dark border-color-secondary-dark bg-color-background-base hover:bg-color-secondary-light hover:text-color-secondary-dark',
        success:
          'text-color-success-dark border-color-success-dark bg-color-background-base hover:bg-color-success-light hover:text-color-success-dark',
        warning:
          'text-color-warning-dark border-color-warning-dark bg-color-background-base hover:bg-color-warning-light hover:text-color-warning-dark',
      },
      size: {
        small: 'text-size-body-xs my-0.5 min-h-2 [&>*>[class^="icon-emp"]]:size-[12px]',
        medium: 'text-size-body-sm min-h-4 [&>*>[class^="icon-emp"]]:size-[20px]',
      },
      variant: {
        filled: 'bg-color-grey-light border-color-grey hover:bg-color-grey',
        outlined: '',
      },
      disabled: {
        true: 'cursor-default opacity-35',
        false: '',
      },
      selected: {
        true: 'bg-color-primary text-color-primary-contrasttext border-color-primary-dark',
        false: '',
      },
    },
    defaultVariants: {
      size: 'medium',
      variant: 'outlined',
      disabled: false,
      selected: false,
    },
    /**
     * Color variants when filled use the following classes:
     * - text: {color}-dark
     * - border: {color}-dark
     * - bg: {color}-light
     * - hover text: text-light
     * - hover bg: {color}-dark
     *
     * When outlined+selected, the following classes are applied:
     * - text: {color}-contrasttext
     * - border: {color}-dark
     * - bg: {color}
     *
     * When filled+selected, the following classes are applied:
     * - text: text-light
     * - border: {color}
     * - bg: {color}-dark
     */
    compoundVariants: [
      /** DEFAULT: FILLED SELECTED */
      {
        selected: true,
        variant: 'filled',
        className: 'bg-color-primary-dark text-color-text-light border-color-primary',
      },
      /** ERROR */
      {
        color: 'error',
        variant: 'filled',
        class:
          'text-color-error-dark hover:text-color-text-light hover:bg-color-error-dark border-color-error-dark bg-color-error-light',
      },
      {
        color: 'error',
        variant: 'filled',
        selected: true,
        class: 'border-color-error bg-color-error-dark text-color-text-light',
      },
      {
        color: 'error',
        variant: 'outlined',
        selected: true,
        class: 'text-color-error-contrasttext border-color-error-dark bg-color-error',
      },
      /** INFO */
      {
        color: 'info',
        variant: 'filled',
        class:
          'text-color-info-dark hover:text-color-text-light hover:bg-color-info-dark border-color-info-dark bg-color-info-light',
      },
      {
        color: 'info',
        variant: 'filled',
        selected: true,
        class: 'border-color-info bg-color-info-dark text-color-text-light',
      },
      {
        color: 'info',
        variant: 'outlined',
        selected: true,
        class: 'text-color-info-contrasttext border-color-info-dark bg-color-info',
      },
      /** PRIMARY */
      {
        color: 'primary',
        variant: 'filled',
        class:
          'text-color-primary-dark hover:text-color-text-light hover:bg-color-primary-dark border-color-primary-dark bg-color-primary-light',
      },
      {
        color: 'primary',
        variant: 'filled',
        selected: true,
        class: 'text-color-text-light border-color-primary bg-color-primary-dark',
      },
      {
        color: 'primary',
        variant: 'outlined',
        selected: true,
        class: 'text-color-primary-contrasttext border-color-primary-dark bg-color-primary',
      },
      /** SECONDARY */
      {
        color: 'secondary',
        variant: 'filled',
        class:
          'text-color-secondary-dark hover:text-color-text-light hover:bg-color-secondary-dark border-color-secondary-dark bg-color-secondary-light',
      },
      {
        color: 'secondary',
        variant: 'filled',
        selected: true,
        class: 'text-color-text-light border-color-secondary bg-color-secondary-dark',
      },
      {
        color: 'secondary',
        variant: 'outlined',
        selected: true,
        class: 'text-color-secondary-contrasttext border-color-secondary-dark bg-color-secondary',
      },
      /** SUCCESS */
      {
        color: 'success',
        variant: 'filled',
        class:
          'text-color-success-dark hover:bg-color-success-dark hover:text-color-text-light border-color-success-dark bg-color-success-light',
      },
      {
        color: 'success',
        variant: 'filled',
        selected: true,
        class: 'text-color-text-light border-color-success bg-color-success-dark',
      },
      {
        color: 'success',
        variant: 'outlined',
        selected: true,
        class: 'text-color-success-contrasttext border-color-success-dark bg-color-success',
      },
      /** WARNING */
      {
        color: 'warning',
        variant: 'filled',
        class:
          'text-color-warning-dark hover:bg-color-warning-dark hover:text-color-text-light border-color-warning-dark bg-color-warning-light',
      },
      {
        color: 'warning',
        variant: 'filled',
        selected: true,
        class: 'text-color-text-light border-color-warning bg-color-warning-dark',
      },
      {
        color: 'warning',
        variant: 'outlined',
        selected: true,
        class: 'text-color-warning-contrasttext border-color-warning-dark bg-color-warning',
      },
    ],
  },
);
